// A report's page: its definition and state, its runs by period with each copy, and its change records,
// all with the server's labels. Also the "Open in Xelerate" link of the report e-mail.
import React, { useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Card,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Loader from "components/Progress";
import { useGetReportHistoryQuery, useGetReportQuery } from "services/scheduledReports";
import { classifyReportError } from "utils/reportErrors";
import { changeText, formatWhen, periodText, scheduleText, scopeText } from "utils/reportDefinition";
import StatusChip from "../StatusChip";
import ReportActions from "../ReportActions";

const title = { fontSize: "15px", fontWeight: "bold", color: "#333333", margin: "0 0 12px 0", textTransform: "uppercase" };
const head = { fontWeight: 600, color: "#B3B4B6" };
const empty = { fontSize: "14px", color: "#c7c7c7", margin: 0 };
const note = { fontSize: "12px", color: "grey", margin: "4px 0 0 0" };

function Fact({ label, children }) {
  return (
    <div style={{ display: "flex", gap: "12px", marginBottom: "8px", fontSize: "14px" }}>
      <span style={{ width: "120px", flexShrink: 0, color: "grey" }}>{label}</span>
      <span style={{ wordBreak: "break-word" }}>{children}</span>
    </div>
  );
}

const countsText = (counts) =>
  Object.entries(counts || {})
    .filter(([, n]) => n)
    .map(([word, n]) => `${n} ${word}`)
    .join(", ");

function RunRow({ run }) {
  const [open, setOpen] = useState(false);
  const deliveries = run.deliveries || [];
  return (
    <>
      <TableRow>
        <TableCell>{periodText(run) || run.periodKey}</TableCell>
        <TableCell>{formatWhen(run.sendAt, run.timeZone)}</TableCell>
        <TableCell>
          <StatusChip state={run.state} label={run.label} />
          {run.sentLate ? <p style={note}>Sent late</p> : null}
          {run.reason ? <p style={note}>{run.reason}</p> : null}
        </TableCell>
        <TableCell>{countsText(run.counts)}</TableCell>
        <TableCell align="right">
          {deliveries.length ? (
            <IconButton size="small" onClick={() => setOpen(!open)} aria-label="Show each copy">
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          ) : null}
        </TableCell>
      </TableRow>
      {open ? (
        <TableRow>
          <TableCell colSpan={5} style={{ backgroundColor: "#fafafa" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={head}>Address</TableCell>
                  <TableCell style={head}>State</TableCell>
                  <TableCell style={head}>Sent at</TableCell>
                  <TableCell style={head}>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveries.map((d) => (
                  <TableRow key={d.address}>
                    <TableCell>{d.address}</TableCell>
                    <TableCell>{d.label}</TableCell>
                    <TableCell>{formatWhen(d.sentAt, run.timeZone)}</TableCell>
                    <TableCell>{d.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

export default function History({ config }) {
  const { id } = useParams();
  const routerHistory = useHistory();
  const metaDataValue = useSelector((state) => state.metaData);
  const token = window.localStorage.getItem("token");
  const reportQuery = useGetReportQuery({ token, id }, { refetchOnMountOrArgChange: true });
  const historyQuery = useGetReportHistoryQuery({ token, id }, { refetchOnMountOrArgChange: true });
  const report = reportQuery.data && reportQuery.data.payload && reportQuery.data.payload.report;

  if (reportQuery.isLoading) return <Loader />;
  if (!report) {
    return (
      <Card style={{ padding: "40px 20px", textAlign: "center" }}>
        <p style={{ fontSize: "15px", color: "#c7c7c7", margin: "0 0 12px 0" }}>
          {reportQuery.error ? classifyReportError(reportQuery.error).message : "This report does not exist, or you cannot see it."}
        </p>
        <Button component={Link} to="/scheduledReports/list" color="secondary">
          Back to the reports
        </Button>
      </Card>
    );
  }

  const zone = report.schedule && report.schedule.timeZone;
  const catalogue = (config && config.sections) || [];
  const sectionNames = (report.sections || []).map((s) => {
    const known = catalogue.find((c) => c.key === s.key);
    return known ? known.title : `${s.key} (no longer available)`;
  });
  const history = (historyQuery.data && historyQuery.data.payload) || {};
  const paused = report.status && report.status.state === "paused";
  const skipped = report.skippedOccurrences || [];
  const failed = report.failedCopies || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Card style={{ padding: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
          <Button component={Link} to="/scheduledReports/list" size="small" color="secondary">
            Back to the reports
          </Button>
          <p style={{ fontSize: "17px", fontWeight: "bold", margin: 0, flex: 1 }}>{report.name}</p>
          <StatusChip state={report.status && report.status.state} label={report.status && report.status.label} />
          <ReportActions report={report} onDeleted={() => routerHistory.push("/scheduledReports/list")} />
        </div>
        <Fact label="Solutions">{scopeText(report.scope, metaDataValue.services)}</Fact>
        {(report.solutionsGone || []).length ? (
          <Fact label="">
            {report.solutionsGone.length === 1 ? "1 solution of this report no longer exists." : `${report.solutionsGone.length} solutions of this report no longer exist.`}
          </Fact>
        ) : null}
        <Fact label="Schedule">{scheduleText(report.schedule)}</Fact>
        <Fact label="Next send">{paused ? "Not while paused" : formatWhen(report.nextSendAt, zone)}</Fact>
        {paused && report.status.since ? <Fact label="Paused since">{formatWhen(report.status.since, zone)}</Fact> : null}
        <Fact label="Sections">{sectionNames.join(", ")}</Fact>
        <Fact label="Recipients">{(report.recipients || []).join(", ") || "No one yet, so this report is not sent."}</Fact>
        {failed.length ? (
          <Fact label="Failed copies">
            {failed.map((c) => `${c.address}${c.reason ? ` (${c.reason})` : ""}`).join(", ")}
          </Fact>
        ) : null}
        {skipped.length ? (
          <Fact label="Skipped">
            {skipped.map((s) => `${formatWhen(s.sendAt, zone)}${s.reason ? ` (${s.reason})` : ""}`).join(", ")}
          </Fact>
        ) : null}
      </Card>

      <Card style={{ padding: "16px" }}>
        <p style={title}>Runs</p>
        {historyQuery.isLoading ? <Loader /> : null}
        {historyQuery.error && !historyQuery.data ? <p style={empty}>{classifyReportError(historyQuery.error).message}</p> : null}
        {historyQuery.data && !(history.runs || []).length ? <p style={empty}>This report has not run yet.</p> : null}
        {(history.runs || []).length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={head}>Period</TableCell>
                <TableCell style={head}>Send time</TableCell>
                <TableCell style={head}>State</TableCell>
                <TableCell style={head}>Copies</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {history.runs.map((run) => (
                <RunRow key={run.id} run={run} />
              ))}
            </TableBody>
          </Table>
        ) : null}
      </Card>

      <Card style={{ padding: "16px" }}>
        <p style={title}>Changes</p>
        {historyQuery.data && !(history.changes || []).length ? <p style={empty}>No changes recorded.</p> : null}
        {(history.changes || []).length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={head}>When</TableCell>
                <TableCell style={head}>By</TableCell>
                <TableCell style={head}>What</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.changes.map((change, i) => (
                <TableRow key={`${change.at}-${i}`}>
                  <TableCell>{formatWhen(change.at, zone)}</TableCell>
                  <TableCell>{change.by}</TableCell>
                  <TableCell>{changeText(change)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </Card>
    </div>
  );
}
